import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Multiselect } from 'react-widgets';
import typeOf from '../../../../helpers/typeOf';
import query from '../../../../helpers/query';
import config from '../../../../config';
import CustomModal from '../../../Modal';

export function GoalsPageFilter(props) {
    const {
        t,
        locales: pLocales,
        access: pAccess,
        statuses: pStatuses,
        searchString: pSearch,
        accessGranted: pAccessGranted,
        offerLocales: pOfferLocales,
        modalIsVisible: pVisible,
        onAction,
        onClose,
        location
    } = props;

    const parsedQuery = useMemo(() => query.parse(location?.query || ''), [location?.query]);

    const [locales, setLocales] = useState(pLocales || []);
    const [access, setAccess] = useState(pAccess || []);
    const [searchString, setSearchString] = useState(parsedQuery.searchString || pSearch || '');
    const [debouncedSearchString, setDebouncedSearchString] = useState(parsedQuery.searchString || pSearch || '');
    const [accessGranted, setAccessGranted] = useState(parsedQuery.accessGranted || pAccessGranted);
    const [offerLocales, setOfferLocales] = useState(parsedQuery.offerLocales || pOfferLocales);
    const [statuses, setStatuses] = useState(pStatuses === null ? [] : pStatuses || [{ value: 'active' }]);
    const [modalIsVisible, setModalIsVisible] = useState(!!pVisible);

    useEffect(() => {
        const urlSearchString = parsedQuery.searchString || '';
        const urlAccessGranted = parsedQuery.accessGranted;
        const urlOfferLocales = parsedQuery.offerLocales;
        
        onAction('init', {
            searchString: urlSearchString,
            accessGranted: urlAccessGranted,
            offerLocales: urlOfferLocales,
            statuses
        });
    }, []);

    useEffect(() => {
        setLocales(pLocales || []);
    }, [pLocales]);

    useEffect(() => {
        setAccess(pAccess || []);
    }, [pAccess]);

    useEffect(() => {
        if (pVisible && !modalIsVisible) setModalIsVisible(true);
    }, [pVisible, modalIsVisible]);

    useEffect(() => {
        if (typeof pSearch === 'string' && pSearch !== searchString) {
            setSearchString(pSearch);
        }
    }, [pSearch, searchString]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchString(searchString);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchString]);

    useEffect(() => {
        const trimmedSearch = debouncedSearchString.trim();
        if (trimmedSearch.length >= 2) {
            fireAction(null, { searchString: trimmedSearch });
        } else {
            fireAction(null, { searchString: '' });
        }
    }, [debouncedSearchString, fireAction]);

    useEffect(() => {
        const urlSearchString = parsedQuery.searchString || '';
        if (urlSearchString !== debouncedSearchString) {
            setDebouncedSearchString(urlSearchString);
        }
    }, [parsedQuery.searchString]);

    const fireAction = useCallback(
        (type, patch) => {
            const base = {
                searchString: debouncedSearchString,
                accessGranted,
                offerLocales,
                statuses,
                page: config.initialPage
            };
            const next = patch ? { ...base, ...patch } : base;
            if (type === 'init') delete next.page;
            onAction(next);
            setModalIsVisible(false);
            onClose && onClose();
        },
        [debouncedSearchString, accessGranted, offerLocales, statuses, onAction, onClose]
    );

    const handleSearchChange = useCallback(
        (e) => {
            const v = e.target.value;
            setSearchString(v);
        },
        []
    );

    const handleAccessSelectChange = useCallback((items) => setAccessGranted(items), []);
    const handleLocalesSelectChange = useCallback((items) => setOfferLocales(items), []);
    const handleStatusesSelectChange = useCallback((items) => setStatuses(items), []);

    const filledCount = useMemo(() => {
        const keys = ['searchString', 'accessGranted', 'offerLocales', 'statuses'];
        let c = 0;
        keys.forEach((k) => {
            const v = { searchString, accessGranted, offerLocales, statuses }[k];
            if (v) {
                if ((typeOf.isArray(v) && v.length) || typeOf.isString(v)) c += 1;
            }
        });
        return c;
    }, [searchString, accessGranted, offerLocales, statuses]);

    const staticNames = t?.offersPage?.goalsList || {
        filter: 'Filter',
        byGoalName: 'By goal name',
        goalName: 'Goal name',
        minSymbols: 'Min 3 symbols',
        byAccess: 'By access',
        byLocales: 'By locales',
        byStatus: 'By status',
        emptyList: 'No options'
    };

    return (
        <div className="filter-construrtor" data-filled={filledCount}>
            <CustomModal
                visible={modalIsVisible}
                title={staticNames.filter}
                areNoControls={false}
                onConfirm={() => fireAction('confirm')}
                onClose={() => { setModalIsVisible(false); onClose && onClose(); }}
            >
                <div>
                    <div className="hidden">
                        <label className="input-label bold" htmlFor="goals-filter-search">
                            {staticNames.byGoalName} <i className="fa fa-search"></i>
                        </label>
                        <input
                            id="goals-filter-search"
                            name="searchString"
                            className="form-control"
                            type="text"
                            placeholder={staticNames.goalName}
                            pattern=".{3,}"
                            title={staticNames.minSymbols}
                            onChange={handleSearchChange}
                            value={searchString}
                        />
                    </div>
                    <div className="form-group">
                        <label className="control-label" id="goals-filter-access-label" htmlFor="goals-filter-access">
                            {staticNames.byAccess} <i className="fa fa-chevron-down"></i>
                        </label>
                        <Multiselect
                            messages={{ emptyList: staticNames.emptyList }}
                            dataKey="value"
                            textField="title"
                            onChange={handleAccessSelectChange}
                            data={access}
                            value={accessGranted}
                            aria-labelledby="goals-filter-access-label"
                            inputProps={{ id: 'goals-filter-access', name: 'accessGranted' }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="control-label" id="goals-filter-locales-label" htmlFor="goals-filter-locales">
                            {staticNames.byLocales} <i className="fa fa-chevron-down"></i>
                        </label>
                        <Multiselect
                            messages={{ emptyList: staticNames.emptyList }}
                            dataKey="value"
                            textField="title"
                            onChange={handleLocalesSelectChange}
                            data={locales}
                            value={offerLocales}
                            aria-labelledby="goals-filter-locales-label"
                            inputProps={{ id: 'goals-filter-locales', name: 'offerLocales' }}
                        />
                    </div>
                    <div className="form-group">
                        <label className="control-label" id="goals-filter-statuses-label" htmlFor="goals-filter-statuses">
                            {staticNames.byStatus} <i className="fa fa-chevron-down"></i>
                        </label>
                        <Multiselect
                            messages={{ emptyList: staticNames.emptyList }}
                            dataKey="value"
                            textField="title"
                            onChange={handleStatusesSelectChange}
                            data={pStatuses}
                            value={statuses}
                            aria-labelledby="goals-filter-statuses-label"
                            inputProps={{ id: 'goals-filter-statuses', name: 'statuses' }}
                        />
                    </div>
                </div>
            </CustomModal>
        </div>
    );
}

GoalsPageFilter.propTypes = {
    locales: PropTypes.array,
    access: PropTypes.array,
    searchString: PropTypes.string,
    accessGranted: PropTypes.any,
    offerLocales: PropTypes.any,
    statuses: PropTypes.any,
    modalIsVisible: PropTypes.bool,
    onAction: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    t: PropTypes.object,
    location: PropTypes.shape({ query: PropTypes.string })
};

GoalsPageFilter.defaultProps = {
    locales: [],
    access: [],
    searchString: '',
    accessGranted: undefined,
    offerLocales: undefined,
    statuses: [],
    modalIsVisible: false
};
